import { useState, useCallback, useEffect, useRef } from 'react';

// Preference keys - add more as needed
export enum PreferenceKey {
  AI_DESCRIPTIONS_ENABLED = 'ai_descriptions_enabled',
  SHOW_DESCRIPTIONS = 'show_descriptions',
  DARK_MODE = 'dark_mode',
  EDIT_MENU_IS_EXPANDED = 'EDIT_MENU_IS_EXPANDED',
  HIDE_WARNING_EDIT_RESTRICTION = 'hide_warning_edit_restriction',
  DEBUG_PUBLISH_ENABLED = 'debug_publish_enabled',
  DEBUG_SAVES_ENABLED = 'debug_saves_enabled',
  OVERVIEW_ITEMS_PER_PAGE = 'overview_items_per_page',
  OVERVIEW_VIEW_MODE = 'overview_view_mode',
}

export interface UsePreferencesOptions {
  storageType?: 'local' | 'session';
  prefix?: string;
}

type PreferenceValue = boolean | string | number | null;
type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T;

/**
 * Hook for managing user preferences with browser storage
 */
export function usePreferences(options: UsePreferencesOptions = {}) {
  const { storageType = 'local', prefix = 'bmls_pref_' } = options;

  const storage = storageType === 'local' ? localStorage : sessionStorage;

  // Get a preference from storage
  const getPreference = useCallback(
    <T extends PreferenceValue>(
      key: PreferenceKey | string,
      defaultValue: T,
    ): T => {
      const prefKey = `${prefix}${key}`;
      const storedValue = storage.getItem(prefKey);

      if (storedValue === null) {
        return defaultValue;
      }

      try {
        return JSON.parse(storedValue) as T;
      } catch {
        // If parsing fails, return the raw string
        return storedValue as unknown as T;
      }
    },
    [storage, prefix],
  );

  // Set a preference in storage
  const setPreference = useCallback(
    <T extends PreferenceValue>(
      key: PreferenceKey | string,
      value: T,
    ): void => {
      const prefKey = `${prefix}${key}`;

      if (value === null) {
        storage.removeItem(prefKey);
      } else {
        storage.setItem(prefKey, JSON.stringify(value));
      }
    },
    [storage, prefix],
  );

  // Create a stateful preference with getter/setter
  const usePreference = <T extends PreferenceValue>(
    key: PreferenceKey | string,
    defaultValue: T,
  ): [Widen<T>, (value: Widen<T>) => void] => {
    const prefKey = `${prefix}${key}`;
    const [value, setValue] = useState<Widen<T>>(
      () => getPreference(key, defaultValue) as Widen<T>,
    );
    // Track the last dispatched value to prevent infinite loops
    const lastDispatchedValue = useRef<Widen<T>>(value);

    // Update storage when value changes
    useEffect(() => {
      // Only dispatch if value actually changed from what we last dispatched
      if (lastDispatchedValue.current === value) {
        return;
      }
      lastDispatchedValue.current = value;
      setPreference(key, value);
      // Dispatch a custom event so other components using this preference can sync
      window.dispatchEvent(
        new CustomEvent('preferenceChange', {
          detail: { key: prefKey, value },
        }),
      );
    }, [key, prefKey, value]);

    // Listen for preference changes from other components
    useEffect(() => {
      const handlePreferenceChange = (event: Event) => {
        const customEvent = event as CustomEvent<{
          key: string;
          value: unknown;
        }>;
        if (customEvent.detail.key === prefKey) {
          const newValue = customEvent.detail.value as Widen<T>;
          // Update our tracking ref so we don't re-dispatch
          lastDispatchedValue.current = newValue;
          setValue(newValue);
        }
      };

      window.addEventListener('preferenceChange', handlePreferenceChange);
      return () => {
        window.removeEventListener('preferenceChange', handlePreferenceChange);
      };
    }, [prefKey]);

    // Return state and setter
    return [value, setValue];
  };

  // Clear a specific preference
  const clearPreference = useCallback(
    (key: PreferenceKey | string): void => {
      const prefKey = `${prefix}${key}`;
      storage.removeItem(prefKey);
    },
    [storage, prefix],
  );

  return {
    getPreference,
    setPreference,
    usePreference,
    clearPreference,
  };
}
